import React, { FormEvent, useState } from 'react';
import {
  Box, Button, Container, FormControl, InputAdornment,
  InputLabel, MenuItem, Select, TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import { getCurLanuagePhone } from '@ivymobi/orbit-core/business/phoneNumber';
import { SendSmsCodeButton } from './smsCode';
import { Save as SaveIcon } from '@mui/icons-material';
import Notistack, { defaultNotistackState, NotistackState } from '../components/Notistack/Notistack';
import useSWR from 'swr';
import { GetResetPhoneMessage, GetResetPhoneMessageArgs } from './api';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import Loader from '../components/loaders';
import { postJSON } from '../api';

interface ResetPhoneCompProps {
  name?: string;
  oldCountryCode: string;
  oldPhone: string;
  newCountryCode: string;
  newPhone: string;
  sendCodeSucces: boolean;
  doSendCode: (countryCode: string, phone: string) => void;
  onSubmit: (code: string) => Promise<void>;
}
export function ResetPhoneComp(props: ResetPhoneCompProps) {
  const [t, i18n] = useTranslation();
  const {
    name, oldCountryCode, oldPhone,
    newCountryCode, newPhone, sendCodeSucces,
    doSendCode, onSubmit
  } = props;

  const [code, setCode] = useState<string>(null);
  const countries = getCountries();
  const labels = getCurLanuagePhone(i18n);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(code);
  };

  return (
    <Container maxWidth='sm'>
      {/* TODO：接口没有name字段 */}
      {/* <TextField name='name' fullWidth disabled
        label={t('YBT-ADMIN.YBT-ADMIN-030101.YBT-ADMIN-03010106')}
        defaultValue={name}
        sx={{ mb: 3 }}
      /> */}

      <Box display='flex' flexWrap='nowrap' alignItems='center' gap={2} marginBottom={3}>
        <FormControl variant='outlined' style={{ width: '30%' }}>
          <InputLabel id="oldPhoneSelectLabel">
            {t('login.country')}
          </InputLabel>
          <Select disabled
            labelId="oldPhoneSelectLabel"
            id="OldPhoneSelect"
            name='countryCode'
            value={oldCountryCode.replace('+', '')}
            label="countryCode"
          >
            {countries.map((country) => (
              <MenuItem key={country}
                value={getCountryCallingCode(country)}
              >
                {labels[country]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField disabled
          id="oldPhoneInput" variant="outlined"
          margin="normal" name="phone"
          label='旧手机号'
          placeholder={t('YBT-ADMIN.YBT-ADMIN-030103.YBT-ADMIN-03010320')}
          defaultValue={oldPhone}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                { oldCountryCode }
              </InputAdornment>
            ),
          }}
          style={{ width: '70%', margin: 0 }}
        />
      </Box>

      <Box display='flex' flexWrap='nowrap' alignItems='center' gap={2} marginBottom={3} >
        <FormControl variant='outlined' style={{ width: '30%' }}>
          <InputLabel id="newPhoneSelectLabel">
            {t('login.country')}
          </InputLabel>
          <Select required disabled name='countryCode'
            labelId="newPhoneSelectLabel"
            id="newPhoneSelect"
            defaultValue={(newCountryCode ?? '').replace('+', '')}
            label="countryCode"
          >
            {countries.map((country) => (
              <MenuItem key={country}
                value={getCountryCallingCode(country)}
              >
                {labels[country]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField required disabled name="phone"
          id="newPhoneInput" variant="outlined"
          margin="normal" label='新手机号'
          placeholder={t('YBT-ADMIN.YBT-ADMIN-030103.YBT-ADMIN-03010320')}
          defaultValue={newPhone ?? ''}
          InputProps={{
            startAdornment: <InputAdornment position="start">
              { newCountryCode }
            </InputAdornment>
          }}
          style={{ width: '70%', margin: 0 }}
        />
      </Box>
      <form onSubmit={handleSubmit} id='resetPhoneForm'>
        <TextField required name="code" fullWidth
          label={t('share.form.code')}
          value={code ?? ''}
          onChange={(e) => setCode(String(e.target.value))}
          sx={{ mb: 3 }}
          inputProps={{
            minLength: 6
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SendSmsCodeButton
                  doSendCode={() => doSendCode(newCountryCode, newPhone)}
                />
              </InputAdornment>
            )
          }}
        />
      </form>
      <Button type='submit' form='resetPhoneForm'
        fullWidth color='primary' variant='contained'
        disabled={!sendCodeSucces}
        startIcon={<SaveIcon />}
      >
        提交
      </Button>

    </Container>
  );
}

export default function ResetPhone() {
  const [t] = useTranslation();
  const history = useHistory();
  const { inv_id } = useParams<{ inv_id: string }>();
  const [sendCodeSucces, setSendSuccess] = useState<boolean>(false);
  const [notistack, setNotistack] = useState<NotistackState>(defaultNotistackState);

  const { data, isLoading } = useSWR(
    GetResetPhoneMessageArgs({ inv_id }),
    GetResetPhoneMessage,
    { suspense: true }
  );

  const {
    info: { logo = null } = {}, tenant_name = null, tnt_id = null,
    old_phone = null, new_phone = null
  } = data;

  const handleSendCode = async (countryCode: string, phone: string) => {
    try {
      await postJSON({
        url: 'user/mobile/code',
        body: {
          countryCode,
          mobile: phone,
        }
      });
      setNotistack((prev) => ({
        ...prev,
        open: true,
        message: '发送成功!'
      }));
      setSendSuccess(true);
    } catch (err) {
      setNotistack((prev) => ({
        ...prev,
        open: true,
        message: err?.message ??  t('Retry'),
        severity: 'error'
      }));
      setSendSuccess(false);
    }
  };

  const handleSubmit = async (code: string) => {
    try {
      await postJSON({
        url: `tenants/${tnt_id}/updatePhone`,
        body: {
          oldPhone: {
            countryCode: old_phone.countryCode,
            mobile: old_phone.mobile
          },
          newPhone: {
            countryCode: new_phone.countryCode,
            mobile: new_phone.mobile
          },
          code: code
        }
      });
      setNotistack((prev) => ({
        ...prev,
        open: true,
        message:  t('Edit Success')
      }));
      setTimeout(() => {
        history.replace('/');
      }, 1000);
    } catch (err) {
      setNotistack((prev) => ({
        ...prev,
        open: true,
        message: err?.message ?? '请重试!',
        severity: 'error'
      }));
    }
  };

  const handleGoHome = () => {
    history.replace('/');
  };

  const handleCloseNotistack = () => {
    setNotistack(defaultNotistackState);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (tnt_id === null) {
    return (
      <Box>
        <Box height='100vh' minHeight={400}
          display='flex' flexDirection='column' alignItems='center'
          justifyContent='center' gap={12.5}
        >
          <Typography textAlign='center' variant='h5'>
            手机号已更换
          </Typography>
          <Button onClick={handleGoHome}>
            返回登录页
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ my: 6 }}>
        <Box display="flex" alignItems="center" justifyContent="center" mb={2} >
          <img style={{ objectFit: 'contain', minWidth: '32px', height: '32px' }}
            src={logo}
          />
          <Typography variant='h5' sx={{px: 1}}>
            {tenant_name}
          </Typography>
        </Box>
        <Typography textAlign='center'>更换手机号</Typography>
      </Box>

      <ResetPhoneComp
        oldCountryCode={old_phone.countryCode}
        oldPhone={old_phone.mobile}
        newCountryCode={new_phone.countryCode}
        newPhone={new_phone.mobile}
        sendCodeSucces={sendCodeSucces}
        doSendCode={handleSendCode}
        onSubmit={handleSubmit}
      />
      <Notistack {...notistack} onClose={handleCloseNotistack} />
    </Box>
  );
}